// Professional Bitcoin address validator.
// Supports: Legacy (P2PKH), P2SH, SegWit (Bech32 P2WPKH / P2WSH), Taproot (Bech32m P2TR)
// Performs full Base58Check + Bech32/Bech32m checksum verification.
//
// Result shape:
//   {
//     status: 'idle' | 'valid' | 'invalid' | 'incomplete',
//     type: string | null,            // e.g. 'Legacy (P2PKH)'
//     network: 'mainnet' | 'testnet' | null,
//     charCount: number,
//     checksum: 'ok' | 'fail' | 'n/a',
//     message: string,                // user-friendly description
//   }

import { base58CheckDecode, BASE58_CHARS } from "./crypto/base58";
import { decodeSegwit, BECH32_CHARS } from "./crypto/bech32";

const IDLE = (charCount = 0) => ({
  status: "idle",
  type: null,
  network: null,
  charCount,
  checksum: "n/a",
  message: "",
});

const TYPE_BY_VERSION = {
  0x00: { kind: "Legacy (P2PKH)", network: "mainnet" },
  0x05: { kind: "P2SH", network: "mainnet" },
  0x6f: { kind: "Legacy (P2PKH, Testnet)", network: "testnet" },
  0xc4: { kind: "P2SH (Testnet)", network: "testnet" },
};

// --- Public per-type validators ---------------------------------------------

export function validateLegacyAddress(addr) {
  return validateBase58Address(addr, { kind: "Legacy (P2PKH)", versions: [0x00, 0x6f], firstChars: ["1", "m", "n"] });
}

export function validateP2SHAddress(addr) {
  return validateBase58Address(addr, { kind: "P2SH", versions: [0x05, 0xc4], firstChars: ["3", "2"] });
}

export function validateBech32Address(addr) {
  const len = addr.length;
  const lower = addr.toLowerCase();
  if (!lower.startsWith("bc1") && !lower.startsWith("tb1")) {
    return { status: "invalid", type: "Bech32", network: null, charCount: len, checksum: "fail", message: "Not a Bech32 address" };
  }
  return decodeAndValidateSegwit(addr);
}

// --- Helpers ----------------------------------------------------------------

function validateBase58Address(addr, opts) {
  const len = addr.length;
  // Detect network from first char
  const first = addr[0];
  const network = first === "m" || first === "n" || first === "2" ? "testnet" : "mainnet";
  const kindBase = opts.kind + (network === "testnet" ? " (Testnet)" : "");

  // Character check first — non-Base58 chars are an immediate fail, even for short input
  for (const ch of addr) {
    if (!BASE58_CHARS.has(ch)) {
      return {
        status: "invalid",
        type: kindBase,
        network,
        charCount: len,
        checksum: "fail",
        message: `Invalid character: "${ch}"`,
      };
    }
  }
  // Length checks (shortest valid base58check BTC addr is 26 chars)
  if (len < 26) {
    return { status: "incomplete", type: kindBase, network, charCount: len, checksum: "n/a", message: "Address too short" };
  }
  if (len > 35) {
    return { status: "invalid", type: kindBase, network, charCount: len, checksum: "fail", message: "Address too long" };
  }
  const payload = base58CheckDecode(addr);
  if (!payload) {
    return { status: "invalid", type: kindBase, network, charCount: len, checksum: "fail", message: "Checksum verification failed" };
  }
  const ver = payload[0];
  const info = TYPE_BY_VERSION[ver];
  if (!info || !opts.versions.includes(ver)) {
    return { status: "invalid", type: kindBase, network, charCount: len, checksum: "fail", message: "Unsupported version byte" };
  }
  return {
    status: "valid",
    type: info.kind,
    network: info.network,
    charCount: len,
    checksum: "ok",
    message: "Valid Bitcoin address",
  };
}

function decodeAndValidateSegwit(addr) {
  const len = addr.length;
  const lower = addr.toLowerCase();
  const network = lower.startsWith("bc1") ? "mainnet" : "testnet";
  // mixed case
  if (addr !== lower && addr !== addr.toUpperCase()) {
    return { status: "invalid", type: "Bech32", network, charCount: len, checksum: "fail", message: "Mixed case is not allowed" };
  }
  // shortest realistic segwit is ~ 14 chars (theoretical), real-world P2WPKH ≥ 42. Treat very short as incomplete.
  if (len < 14) {
    return { status: "incomplete", type: "Bech32", network, charCount: len, checksum: "n/a", message: "Address incomplete" };
  }
  // Validate charset of data part
  const dataPart = lower.slice(lower.indexOf("1") + 1);
  for (const ch of dataPart) {
    if (!BECH32_CHARS.has(ch)) {
      // bad chars → invalid (not incomplete)
      return { status: "invalid", type: "Bech32", network, charCount: len, checksum: "fail", message: `Invalid character: "${ch}"` };
    }
  }
  const seg = decodeSegwit(lower);
  if (!seg) {
    // Distinguish incomplete (too short) vs broken
    if (len < 42) {
      return { status: "incomplete", type: "Bech32", network, charCount: len, checksum: "n/a", message: "Address incomplete" };
    }
    return { status: "invalid", type: "Bech32", network, charCount: len, checksum: "fail", message: "Bech32 checksum failed" };
  }
  let type;
  if (seg.version === 0 && seg.program.length === 20) type = "SegWit v0 (P2WPKH)";
  else if (seg.version === 0 && seg.program.length === 32) type = "SegWit v0 (P2WSH)";
  else if (seg.version === 1 && seg.program.length === 32) type = "Taproot (P2TR)";
  else type = `SegWit v${seg.version}`;
  return {
    status: "valid",
    type,
    network,
    charCount: len,
    checksum: "ok",
    message: "Valid Bitcoin address",
  };
}

// --- Detection / dispatcher --------------------------------------------------

export function detectAddressType(addr) {
  if (!addr) return null;
  const a = addr.trim();
  if (a.length === 0) return null;
  const lower = a.toLowerCase();
  if (lower.startsWith("bc1p")) return "Taproot";
  if (lower.startsWith("bc1q") || lower.startsWith("bc1")) return "SegWit (Bech32)";
  if (lower.startsWith("tb1")) return "SegWit Testnet";
  const c = a[0];
  if (c === "1") return "Legacy (P2PKH)";
  if (c === "3") return "P2SH";
  if (c === "m" || c === "n") return "Legacy Testnet";
  if (c === "2") return "P2SH Testnet";
  return null;
}

export function validateChecksum(addr) {
  const r = validateBTC(addr);
  return r.checksum === "ok";
}

// Main entrypoint
export function validateBTC(input) {
  const addr = (input || "").trim();
  const len = addr.length;
  if (len === 0) return IDLE();

  const lower = addr.toLowerCase();
  const first = addr[0];

  // Bech32 / Bech32m branch (SegWit & Taproot)
  if (lower.startsWith("bc1") || lower.startsWith("tb1")) {
    return decodeAndValidateSegwit(addr);
  }
  // Base58Check branch
  if (first === "1" || first === "m" || first === "n") {
    return validateBase58Address(addr, {
      kind: "Legacy (P2PKH)",
      versions: [0x00, 0x6f],
      firstChars: ["1", "m", "n"],
    });
  }
  if (first === "3" || first === "2") {
    return validateBase58Address(addr, {
      kind: "P2SH",
      versions: [0x05, 0xc4],
      firstChars: ["3", "2"],
    });
  }
  // Partial "b", "bc", "bc1" → incomplete
  if (lower === "b" || lower === "bc" || lower === "bc1") {
    return { status: "incomplete", type: "Bech32", network: "mainnet", charCount: len, checksum: "n/a", message: "Address incomplete" };
  }
  return {
    status: "invalid",
    type: null,
    network: null,
    charCount: len,
    checksum: "fail",
    message: "Not a recognised Bitcoin address",
  };
}
